import React, {
  useEffect,
  useState,
  FunctionComponent,
} from 'react';

import renderModal from '@components/show_page/modals/renderModal';
import InputContainer from '@components/library/input_container/InputContainer';
import { InputType } from '@components/library/inputs/Input.props';
import { BFLoader } from '@components/common/loader/main';
import FilestackUploader from '@components/common/filestack_uploader/main';
import { FileStackOnUpload } from '@components/common/filestack_uploader/FileStackTypes';
import {
  CloseButton,
  PrimaryButton,
  TextWarningButton,
} from '@components/library/button/index';
import { getBrandguide } from '@api/v4/resources/brandguides';
import { updateBrandguide } from '@api/v3/brandguides';

interface SettingsModalProps {
  brandguideKey: string;
  closeModal: () => void;
}

interface Attributes {
  cardImage: string;
  name: string;
  slug: string;
}

const defaultData: Attributes = { cardImage: '', name: '', slug: '' };

const SettingsModal: FunctionComponent<SettingsModalProps> = ({
  brandguideKey,
  closeModal,
}) => {
  const [initialData, setInitialData] = useState(defaultData);
  const [updatedData, setUpdatedData] = useState(defaultData);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleSubmit = async (): Promise<void> => {
    const { cardImage, name, slug } = updatedData;
    setIsSubmitting(true);

    try {
      await updateBrandguide({
        initialSlug: initialData.slug,
        ...(initialData.cardImage !== cardImage) && { cardImage },
        ...(initialData.name !== name) && { name },
        ...(initialData.slug !== slug) && { slug },
      });
      Notify.create({
        title: t('Brandguide settings updated! Refreshing...'),
        type: "success"
      });
      setIsSubmitDisabled(true); // maintain disabled button while page reloads
      window.location.reload();
    } catch (error) {
      Notify.create({
        title: t('An error occurred while updating this Brandguide. Please try again or contact support.'),
        type: "error",
        ...error.errors && { body: error.errors },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateInput = (e: InputChangeEvent): void => {
    e.persist();
    const { name, value } = e.target;
    setUpdatedData((prevState) => ({ ...prevState, [name]: value }));
  };

  const checkDiff = (): void => {
    const { cardImage: cardImageInitial, name: nameInitial, slug: slugInitial } = initialData;
    const { cardImage, name, slug } = updatedData;
    setIsSubmitDisabled(cardImageInitial === cardImage && name === nameInitial && slug === slugInitial);
  };

  const fetchBrandguide = async (): Promise<void> => {
    try {
      const { attributes } = await getBrandguide(brandguideKey);
      const { card_image: cardImage, name, slug } = attributes;
      setInitialData({ cardImage, name, slug });
      setUpdatedData({ cardImage, name, slug });
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBrandguide();
    return closeModal;
  }, []);

  useEffect(() => {
    checkDiff();
  }, [updatedData]);

  return (
    <div className="modal-content-wrapper brandguide-modal">
      <div className="modal-content-wrapper__header">
        <span className="bff-settings icon" />
        <h3>{t("Brandguide Settings")}</h3>
        <CloseButton onClick={closeModal} />
      </div>
      {isLoading ? (
        <div className="loader-container"><BFLoader /></div>
      ) : (
        <div className="modal-content-wrapper__body">
          <InputContainer
            attributes={{
              name: "name",
              onChange: handleUpdateInput,
              type: InputType.Text,
            }}
            input={{ value: updatedData?.name }}
            labelCopy={t("Name")}
          />
          <InputContainer
            attributes={{
              name: "slug",
              onChange: handleUpdateInput,
              type: InputType.Text,
            }}
            input={{ value: updatedData?.slug }}
            labelCopy={t("Unique URL")}
          />
          <div className="brandguide-modal__filestack-container">
            <div className="brandguide-modal__filestack-container--description">
              <h3>{t("Card Image")}</h3>
              <p>{t("Recommended size: 260px x 130px")}</p>
            </div>
            {updatedData?.cardImage ? (
              <div className="brandguide-modal__filestack-container--thumbnail">
                <div className="brandguide-modal__filestack-container--thumbnail-container">
                  <img className="brandguide-modal__filestack-container--thumbnail--image" src={updatedData?.cardImage} alt="thumbnail" />
                </div>
                <TextWarningButton
                  icon="bff-trash"
                  onClick={(): void => setUpdatedData((prevState) => ({ ...prevState, cardImage: '' }))}
                />
              </div>
            ) : (
              <FilestackUploader
                options={{
                  maxFiles: 1,
                  accept: "image/*",
                  transformations: { crop: { aspectRatio: (260 / 130), force: true } }
                }}
                type="area"
                onSuccess={(files: FileStackOnUpload): void => setUpdatedData((prevState) => ({ ...prevState, cardImage: files?.filesUploaded?.[0]?.url }))}
              />
            )}
          </div>
          <div className="button-container">
            <PrimaryButton
              disabled={isSubmitDisabled}
              isLoading={isSubmitting}
              onClick={handleSubmit}
              size="medium"
            >
              {t('Update Settings')}
            </PrimaryButton>
          </div>
        </div>
      )}
    </div>
  );
};

const ModalComponent = renderModal(SettingsModal, 'brandguide-settings-modal');
export default ModalComponent;
